import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getEnvVar } from '../../../environments/env-loader';

export interface ProfileResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    email: string;
    name?: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    birthDate?: string;
    timezone?: string;
    profilePic?: string;
    profilePicPresignedUrl?: string; // Add presigned URL for secure access
    phone?: string;
    primaryPhone?: string;
    address?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    country?: string;
    role?: string;
    status?: string;
    lastLoginAt?: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  timezone?: string;
}

export interface UpdateContactAddressRequest {
  primaryPhone?: string;
  address?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  language?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface DeleteAccountRequest {
  password: string;
  confirmDeletion: boolean;
}

export interface ContactAddressResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    email: string;
    name: string;
    username: string;
    profilePic?: string;
    primaryPhone?: string;
    address?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    country?: string;
    lastLoginAt?: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface SubscriptionDetailsResponse {
  success: boolean;
  message: string;
  data: {
    hasActiveSubscription: boolean;
    subscription: {
      id: number;
      stripeId: string;
      status: string;
      plan: {
        id: number;
        name: string;
        price: number;
        currency: string;
        interval: string;
        description?: string;
      };
      addons: {
        id: number;
        name: string;
        price: number;
        currency: string;
        description?: string;
      }[];
      totalAmount: number;
      currency: string;
      packagePurchasedAt: string;
      subscriptionPeriod: {
        start: string;
        end: string;
      };
      nextRenewalDate?: string;
      isCanceled: boolean;
      canceledAt?: string;
      gracePeriodDays?: number;
    } | null;
  };
}

export interface BillingHistoryResponse {
  success: boolean;
  message: string;
  data: {
    transactions: {
      id: number;
      stripeId: string;
      transactionType: 'SUBSCRIPTION' | 'CANCELLATION' | 'TRIAL';
      status: string;
      plan: {
        id: number;
        name: string;
        price: number;
        currency: string;
        interval: string;
        description?: string;
      };
      addons: {
        id: number;
        name: string;
        price: number;
        currency: string;
        description?: string;
      }[];
      totalAmount: number;
      currency: string;
      transactionDate: string;
      subscriptionPeriod: {
        start: string;
        end: string;
      };
      canceledAt?: string;
      isActive: boolean;
      billingInfo: {
        name: string;
        address: string;
        city: string;
        state: string;
        zip: string;
        country: string;
      };
    }[];
    totalTransactions: number;
    totalAmount: number;
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private readonly API_URL = getEnvVar('REACT_APP_API_URL', environment.apiUrl);

  constructor(private http: HttpClient) {}

  /**
   * Get current user profile
   */
  getProfile(): Observable<ProfileResponse> {
    return this.http.get<ProfileResponse>(`${this.API_URL}/auth/me`);
  }

  /**
   * Update user profile (personal information)
   */
  updateProfile(profileData: UpdateProfileRequest): Observable<ProfileResponse> {
    return this.http.put<ProfileResponse>(`${this.API_URL}/auth/profile`, profileData);
  }

  /**
   * Update profile with photo upload
   */
  updateProfileWithPhoto(formData: FormData): Observable<ProfileResponse> {
    return this.http.put<ProfileResponse>(`${this.API_URL}/auth/profile`, formData);
  }

  /**
   * Update contact and address information
   */
  updateContactAddress(contactData: UpdateContactAddressRequest): Observable<ContactAddressResponse> {
    return this.http.put<ContactAddressResponse>(`${this.API_URL}/auth/contact-info`, contactData);
  }

  /**
   * Delete user account
   */
  deleteAccount(deleteData: DeleteAccountRequest): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.API_URL}/auth/delete-account`, {
      body: deleteData
    });
  }

  /**
   * Format last login time
   */
  formatLastLogin(lastLoginAt?: string): string {
    if (!lastLoginAt) return 'Never';
    
    const now = new Date();
    const lastLogin = new Date(lastLoginAt);
    const diffMs = now.getTime() - lastLogin.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  /**
   * Validate phone number format
   */
  validatePhoneNumber(phone: string): boolean {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  }

  /**
   * Change user password
   */
  changePassword(passwordData: ChangePasswordRequest): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/auth/change-password`, passwordData);
  }

  /**
   * Get subscription details
   */
  getSubscriptionDetails(): Observable<SubscriptionDetailsResponse> {
    return this.http.get<SubscriptionDetailsResponse>(`${this.API_URL}/subscription/details`);
  }

  /**
   * Get billing history
   */
  getBillingHistory(page: number = 1, limit: number = 10): Observable<BillingHistoryResponse> {
    return this.http.get<BillingHistoryResponse>(`${this.API_URL}/subscription/billing-history?page=${page}&limit=${limit}`);
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Calculate days until expiration
   */
  getDaysUntilExpiration(endDate: string): number {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Get full URL for profile image
   */
  getProfileImageUrl(profilePic?: string): string | null {
    if (!profilePic) return null;
    
    // If it's already a full URL, return as is
    if (profilePic.startsWith('http://') || profilePic.startsWith('https://')) {
      return profilePic;
    }
    
    // If it's a relative path, return as is (proxy will handle it)
    if (profilePic.startsWith('/')) {
      return profilePic;
    }
    
    // If it's just a filename, use relative path for proxy
    return `/uploads/profiles/${profilePic}`;
  }

  /**
   * Get the best URL for displaying profile pictures
   * Prefers presignedUrl for secure access, falls back to profilePic
   */
  getDisplayUrl(profileData: { profilePic?: string; profilePicPresignedUrl?: string }): string | null {
    // Prefer presigned URL for secure access
    if (profileData.profilePicPresignedUrl) {
      return profileData.profilePicPresignedUrl;
    }
    
    // Fall back to regular profile picture URL
    return this.getProfileImageUrl(profileData.profilePic);
  }
}

